/* eslint-disable complexity */
import { SkeletonLoader } from '@design';
import { CitationsSection } from '@terminal/components/citations-section/CitationsSection';
import { TextSection } from '@terminal/components/text-section/TextSection';
import { ImpactedCompanies } from '@terminal/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '@terminal/modules/thematic-focus/ThematicFocus';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { useEarningsInsightsFilter } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter';
import { useGetEarningsInsightPreview } from '@terminal/react-query-hooks/insights/useGetEarningsInsightPreview';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ScheduleDemoBanner } from '@terminal/views/insight-feed/components/schedule-demo-banner/ScheduleDemoBanner';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import sharedStyles from '../EarningsInsightWidget.module.scss';
import { EpsExpectationsSection } from './components/eps-expectations-section/EpsExpectationsSection';
import { KpisTable } from './components/kpis-table/KpisTable';
import { PreviewOverview } from './components/PreviewOverview';
import styles from './EarningsPreview.module.scss';

export interface EarningsPreviewProps {
  expandCitations: boolean;
}

export const EarningsPreview = ({ expandCitations }: EarningsPreviewProps) => {
  const { t } = useTranslation(['earnings']);
  const containerRef = useRef<HTMLDivElement>(null);
  const { activeEntityTag, earningsId } = useEarningsInsightDrawer();
  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: entityData,
    isFetching,
    isError: isEntityError,
    error,
  } = useEntity(activeEntityTag ?? '');
  const { previewItems } = useSidebarItems();

  const {
    data: previewData,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewError,
  } = useGetEarningsInsightPreview(
    earningsInsightsData?.find(insight => insight.type === 'preview')?.id ?? ''
  );

  const isLoading =
    isFetching || isPreviewFetching || isEarningsInsightsFetching;

  const isError = isPreviewError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return (
      <SkeletonLoader
        className={styles.loadingSkeletonWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '64px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '136px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError || !entityData?.data) {
    throw new Error(
      error?.message || previewError?.message || earningsInsightsError?.message
    );
  }

  if (!previewData) {
    return (
      <div className={sharedStyles.emptyContainer}>
        <div className={sharedStyles.emptyWrapper}>
          <img
            className={sharedStyles.emptyImage}
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <div className={sharedStyles.emptyTitle}>
            {t('earnings:earningsInsight.drawer.earningsPreview.emptyTitle')}
          </div>
        </div>
      </div>
    );
  }

  const entity = entityData.data;

  return (
    <div className={styles.wrapper}>
      <div className={styles.separator}>
        <div className={styles.leftContainer}>
          <div
            ref={containerRef}
            data-print-scrollable
            className={clsx(styles.flexbox)}
          >
            <div
              ref={previewItems[0].ref}
              data-testid="preview-overview"
              className={styles.section}
            >
              <PreviewOverview
                title={previewData.title}
                activeEntityTag={activeEntityTag ?? ''}
                publishedAt={previewData.created_at}
                previewEarningsInsight={earningsInsightsData?.find(
                  insight => insight.type === 'preview'
                )}
              />
            </div>
            <div
              ref={previewItems[1].ref}
              data-testid="earnings-reaction-scenario"
              className={styles.section}
            >
              {earningsId && (
                <EpsExpectationsSection
                  earningsId={earningsId}
                  entity={entity}
                />
              )}
              <div className={styles.textSectionWrapper}>
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.sentimentAndExpectationsSetup'
                  )}
                  textContent={previewData.historical_context}
                />
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.seasonality'
                  )}
                  textContent={previewData.seasonality}
                />
              </div>
            </div>
            <div
              ref={previewItems[2].ref}
              data-testid="kpis"
              className={styles.section}
            >
              <KpisTable kpisData={previewData.kpis} />
            </div>
            <div
              ref={previewItems[3].ref}
              data-testid="thematic-focus"
              className={styles.section}
            >
              <ThematicFocus
                themes={previewData.themes}
                otherThemes={previewData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.thematicFocusPreview'
                )}
                source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
              />
            </div>
            <div
              ref={previewItems[4].ref}
              data-testid="key-items"
              className={styles.section}
            >
              <TextSection
                useBulletPoints
                variant="primary"
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.keyItems'
                )}
                textContent={previewData.key_items}
              />
            </div>
            <div
              ref={previewItems[5].ref}
              data-testid="investment-outlook"
              className={styles.section}
            >
              <BullBearPoints
                data={previewData.investment_outlook}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.investmentOutlook'
                )}
              />
            </div>
            <div
              ref={previewItems[6].ref}
              data-testid="impacted-companies"
              className={styles.section}
            >
              <ImpactedCompanies
                impactedCompanies={previewData.impacted_companies}
                source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
                title={t('earnings:earningsInsight.drawer.impactedCompanies')}
                insightCreatedAt={previewData.created_at}
              />
            </div>
            <div data-testid="citations" className={styles.section}>
              {previewData?.citations?.length > 0 && (
                <CitationsSection
                  title={t('earningsInsight.drawer.webLinks')}
                  count={previewData.citations.length ?? 0}
                  items={previewData.citations ?? []}
                  isExpanded={expandCitations}
                />
              )}
            </div>
            <ScheduleDemoBanner />
          </div>
        </div>
      </div>
    </div>
  );
};
